import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X } from 'lucide-react';

const navLinks = [
  { label: 'About', href: '#about' },
  { label: 'Technology', href: '#technology' },
  { label: 'Features', href: '#features' },
  { label: 'Comparison', href: '#comparison' },
  { label: 'Products', href: '/products' },
  { label: 'FAQs', href: '/faqs' },
  { label: 'Publications', href: '/publications' },
  { label: 'Blogs', href: '/blogs' },
  { label: 'Contact', href: '#contact' },
];

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const normalizedPath = window.location.pathname.replace(/\/+$/, '') || '/';
  const isHomePage = normalizedPath === '/';

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleNavClick = (e, href) => {
    setMenuOpen(false);
    if (!href.startsWith('#') || !isHomePage) return;
    e.preventDefault();
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  const resolveHref = (href) => {
    if (!href.startsWith('#')) return href;
    return isHomePage ? href : `/${href}`;
  };

  return (
    <motion.nav
      data-testid="navbar"
      initial={{ y: -80 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.6, ease: 'easeOut' }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled ? 'glass shadow-lg' : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 lg:h-20">
          {/* Logo */}
          <a
            href={isHomePage ? '#hero' : '/'}
            onClick={(e) => {
              if (isHomePage) {
                handleNavClick(e, '#hero');
              } else {
                setMenuOpen(false);
              }
            }}
            className="flex items-center gap-2.5 group"
            data-testid="nav-logo"
          >
            <img src="/logo.png" alt="ECPW Logo" className="h-14 w-14 object-contain" />
            <span className="font-heading font-bold text-sm lg:text-base text-white tracking-tight">
              ECPW
            </span>
          </a>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={resolveHref(link.href)}
                onClick={(e) => handleNavClick(e, link.href)}
                data-testid={`nav-link-${link.label.toLowerCase()}`}
                className="text-sm text-slate-400 hover:text-cyan-400 transition-colors duration-300 font-medium tracking-wide"
              >
                {link.label}
              </a>
            ))}
            <a
              href={resolveHref('#contact')}
              onClick={(e) => handleNavClick(e, '#contact')}
              data-testid="nav-cta-demo"
              className="text-sm font-semibold px-5 py-2 rounded-full bg-cyan-500/10 text-cyan-400 border border-cyan-500/30 hover:bg-cyan-500/20 hover:border-cyan-400/50 transition-all duration-300"
            >
              Request Demo
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setMenuOpen(!menuOpen)}
            className="md:hidden text-white p-2"
          >
            {menuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {menuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden glass border-t border-white/5"
            data-testid="mobile-menu"
          >
            <div className="px-4 py-6 space-y-4">
              {navLinks.map((link) => (
                <a
                  key={link.href}
                  href={resolveHref(link.href)}
                  onClick={(e) => handleNavClick(e, link.href)}
                  className="block text-slate-300 hover:text-cyan-400 transition-colors text-sm font-medium"
                >
                  {link.label}
                </a>
              ))}
              <a
                href={resolveHref('#contact')}
                onClick={(e) => handleNavClick(e, '#contact')}
                className="block text-center text-sm font-semibold px-5 py-2.5 rounded-full bg-cyan-500/10 text-cyan-400 border border-cyan-500/30"
              >
                Request Demo
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.nav>
  );
};

export default Navbar;
